import { useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { WelderPerformance } from '../../types/stats';
import { formatCount } from '../../utils/format';
import clsx from 'clsx';

interface WelderPerformanceDashboardProps {
  data: WelderPerformance[];
}

type ChartView = 'overview' | 'total-welds' | 'top-performers';

export const WelderPerformanceDashboard = ({ data }: WelderPerformanceDashboardProps) => {
  const [activeView, setActiveView] = useState<ChartView>('overview');

  // Check if we have both accepted and rejected data
  const hasBothAcceptedAndRejected = useMemo(() => {
    if (!data || data.length === 0) return false;
    const hasAccepted = data.some(w => w.accepted > 0);
    const hasRejected = data.some(w => w.rejected > 0);
    return hasAccepted && hasRejected;
  }, [data]);

  const sortedByDefectRate = useMemo(() => {
    if (!data) return [];
    return [...data].sort((a, b) => b.defectRate - a.defectRate);
  }, [data]);

  const sortedByTotalWelds = useMemo(() => {
    if (!data) return [];
    return [...data].sort((a, b) => b.totalWelds - a.totalWelds);
  }, [data]);

  // Overview Chart (Accepted/Rejected with optional Defect Rate)
  const overviewOption = useMemo(() => {
    if (!data || data.length === 0) return null;

    const sortedData = sortedByDefectRate;
    const welders = sortedData.map((item) => `Welder ${item.welder}`);
    const accepted = sortedData.map((item) => item.accepted);
    const rejected = sortedData.map((item) => item.rejected);
    const defectRates = sortedData.map((item) => item.defectRate);

    const legendData = ['Accepted', 'Rejected'];
    if (hasBothAcceptedAndRejected) {
      legendData.push('Defect Rate %');
    }

    const series: any[] = [
      {
        name: 'Accepted',
        type: 'bar',
        data: accepted,
        itemStyle: { color: '#22c55e' },
        stack: 'welds',
      },
      {
        name: 'Rejected',
        type: 'bar',
        data: rejected,
        itemStyle: { color: '#ef4444' },
        stack: 'welds',
      },
    ];

    if (hasBothAcceptedAndRejected) {
      series.push({
        name: 'Defect Rate %',
        type: 'line',
        yAxisIndex: 1,
        data: defectRates,
        itemStyle: { color: '#f59e0b' },
        lineStyle: { width: 3 },
        symbol: 'circle',
        symbolSize: 8,
      });
    }

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any) => {
          const index = params[0].dataIndex;
          const welder = sortedData[index];
          let tooltip = `
            <strong>Welder ${welder.welder}</strong><br/>
            Total Welds: ${formatCount(welder.totalWelds)}<br/>
            Accepted: ${formatCount(welder.accepted)}<br/>
            Rejected: ${formatCount(welder.rejected)}<br/>
          `;
          if (hasBothAcceptedAndRejected) {
            tooltip += `Defect Rate: ${welder.defectRate.toFixed(2)}%<br/>`;
          }
          if (welder.topDefectType) {
            tooltip += `Top Defect: ${welder.topDefectType}`;
          }
          return tooltip;
        },
      },
      legend: {
        data: legendData,
        top: 10,
      },
      grid: { left: 80, right: hasBothAcceptedAndRejected ? 80 : 40, top: 60, bottom: 100 },
      xAxis: {
        type: 'category',
        data: welders,
        axisLabel: { rotate: 45 },
      },
      yAxis: [
        {
          type: 'value',
          name: 'Number of Welds',
          position: 'left',
        },
        ...(hasBothAcceptedAndRejected ? [{
          type: 'value',
          name: 'Defect Rate (%)',
          position: 'right',
          max: 100,
        }] : []),
      ],
      series,
    } as echarts.EChartsOption;
  }, [data, sortedByDefectRate, hasBothAcceptedAndRejected]);

  // Total Welds Comparison Chart
  const totalWeldsOption = useMemo(() => {
    if (!data || data.length === 0) return null;

    const sortedData = sortedByTotalWelds; // Show all welders to match Overview
    const welders = sortedData.map((item) => `Welder ${item.welder}`);
    const totalWelds = sortedData.map((item) => item.totalWelds);

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any) => {
          const index = params[0].dataIndex;
          const welder = sortedData[index];
          return `
            <strong>Welder ${welder.welder}</strong><br/>
            Total Welds: ${formatCount(welder.totalWelds)}<br/>
            Accepted: ${formatCount(welder.accepted)}<br/>
            Rejected: ${formatCount(welder.rejected)}<br/>
            ${welder.topDefectType ? `Top Defect: ${welder.topDefectType}` : ''}
          `;
        },
      },
      grid: { left: 80, right: 40, top: 40, bottom: 100 },
      xAxis: {
        type: 'category',
        data: welders,
        axisLabel: { rotate: 45 },
      },
      yAxis: {
        type: 'value',
        name: 'Total Welds',
      },
      series: [
        {
          name: 'Total Welds',
          type: 'bar',
          data: totalWelds,
          itemStyle: { color: '#3b82f6' },
        },
      ],
    } as echarts.EChartsOption;
  }, [data, sortedByTotalWelds]);

  // Top/Bottom Performers Comparison
  const topPerformersOption = useMemo(() => {
    if (!data || data.length === 0 || !hasBothAcceptedAndRejected) return null;

    const top5 = sortedByDefectRate.slice(0, 5);
    const bottom5 = sortedByDefectRate.slice(-5).reverse();

    const topWelders = top5.map((item) => `Welder ${item.welder}`);
    const topRates = top5.map((item) => item.defectRate);
    const bottomWelders = bottom5.map((item) => `Welder ${item.welder}`);
    const bottomRates = bottom5.map((item) => item.defectRate);

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
      },
      legend: {
        data: ['Top 5 (Highest Defect Rate)', 'Bottom 5 (Lowest Defect Rate)'],
        top: 10,
      },
      grid: [
        { left: '50%', right: '5%', top: '15%', bottom: '50%' },
        { left: '5%', right: '50%', top: '15%', bottom: '50%' },
      ],
      xAxis: [
        {
          type: 'value',
          gridIndex: 0,
          name: 'Defect Rate (%)',
          max: 100,
        },
        {
          type: 'value',
          gridIndex: 1,
          name: 'Defect Rate (%)',
          max: 100,
        },
      ],
      yAxis: [
        {
          type: 'category',
          gridIndex: 0,
          data: topWelders,
          inverse: true,
        },
        {
          type: 'category',
          gridIndex: 1,
          data: bottomWelders,
          inverse: true,
        },
      ],
      series: [
        {
          name: 'Top 5 (Highest Defect Rate)',
          type: 'bar',
          xAxisIndex: 0,
          yAxisIndex: 0,
          data: topRates,
          itemStyle: { color: '#ef4444' },
        },
        {
          name: 'Bottom 5 (Lowest Defect Rate)',
          type: 'bar',
          xAxisIndex: 1,
          yAxisIndex: 1,
          data: bottomRates,
          itemStyle: { color: '#22c55e' },
        },
      ],
    } as echarts.EChartsOption;
  }, [data, sortedByDefectRate, hasBothAcceptedAndRejected]);

  if (!data || data.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500">
        No welder performance data available.
      </div>
    );
  }

  const renderChart = () => {
    switch (activeView) {
      case 'overview':
        return overviewOption ? (
          <ReactECharts 
            option={overviewOption} 
            style={{ height: 500 }} 
            key="welder-overview"
            notMerge={true}
            lazyUpdate={true}
          />
        ) : null;
      case 'total-welds':
        return totalWeldsOption ? (
          <ReactECharts 
            option={totalWeldsOption} 
            style={{ height: 500 }} 
            key="welder-total-welds"
            notMerge={true}
            lazyUpdate={true}
          />
        ) : null;
      case 'top-performers':
        return topPerformersOption ? (
          <ReactECharts 
            option={topPerformersOption} 
            style={{ height: 500 }} 
            key="welder-top-performers"
            notMerge={true}
            lazyUpdate={true}
          />
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500 text-center">
            <p>Top/Bottom performers comparison requires both accepted and rejected data.</p>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900">Welder Performance Dashboard</h3>
        <div className="flex gap-2">
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'total-welds', label: 'Total Welds' },
            { id: 'top-performers', label: 'Top/Bottom Performers', disabled: !hasBothAcceptedAndRejected },
          ].map((view) => (
            <button
              key={view.id}
              onClick={() => !view.disabled && setActiveView(view.id as ChartView)}
              disabled={view.disabled}
              className={clsx(
                'rounded-full px-3 py-1.5 text-sm font-medium transition border',
                activeView === view.id
                  ? 'text-galvanizing-green bg-[rgba(31,154,58,0.08)]'
                  : view.disabled
                  ? 'text-slate-300 cursor-not-allowed border-slate-200 bg-white'
                  : 'text-slate-600 bg-white border-slate-200 hover:bg-slate-50 hover:text-slate-900',
              )}
            >
              {view.label}
            </button>
          ))}
        </div>
      </div>
      {renderChart()}
    </div>
  );
};

